/**
 * TermsOfService.tsx — Public terms of service.
 * Rebuilt in Pass 7.
 */

import { LegalPageLayout, Section, Bullets, A } from './LegalPageLayout';

export default function TermsOfService() {
  return (
    <LegalPageLayout pageTitle="Terms of Service" lastUpdated="April 2026">
      <Section title="1. Service description">
        Meridian Registry provides a centralized entity resolution platform for FMCG
        retail outlets. Our service ingests client outlet databases, normalizes and
        deduplicates records using a multi-level matching engine, and assigns permanent
        Census IDs to every unique physical outlet. Clients receive enriched data
        exports and API access to the master registry.
      </Section>

      <Section title="2. Client obligations">
        <Bullets items={[
          { label: 'Data accuracy', text: 'You are responsible for the accuracy and completeness of outlet data submitted to Meridian. Intentionally submitting false or misleading data is prohibited.' },
          { label: 'Authorized use', text: `You may only submit data that you have the legal right to share. You must not use the platform to reverse-engineer another client's distribution network.` },
          { label: 'API usage', text: 'API keys are confidential and non-transferable. You must not share your API key or exceed published rate limits. Automated scraping of the full registry is prohibited.' },
          { label: 'Compliance', text: 'You agree to comply with all applicable local laws and regulations when using Meridian data in your operations.' },
        ]} />
      </Section>

      <Section title="3. Intellectual property">
        Census IDs, the Meridian canonical registry, matching algorithms, and all
        derived data products are the intellectual property of Meridian Registry.
        Clients receive a license to use Census IDs within their own systems for the
        duration of their service agreement. The Meridian name, logo, and brand assets
        may not be used without written permission.
      </Section>

      <Section title="4. Data ownership">
        You retain full ownership of the raw outlet data you submit to Meridian. We do
        not claim ownership of your proprietary data. However, normalized and
        deduplicated records that become part of the canonical Meridian registry are
        Meridian's intellectual property. You may export your enriched data (your
        records augmented with Census IDs) at any time.
      </Section>

      <Section title="5. Service availability">
        We strive to maintain high availability but do not guarantee uninterrupted
        access. Scheduled maintenance windows will be communicated in advance. We
        reserve the right to modify, suspend, or discontinue features with reasonable
        notice.
      </Section>

      <Section title="6. Limitation of liability">
        Meridian Registry is provided "as is" without warranty of any kind. We are not
        liable for indirect, incidental, or consequential damages arising from use of
        the platform, including but not limited to lost revenue, data loss, or business
        interruption. Our total liability shall not exceed the fees paid by you in the
        12 months preceding the claim. Matching results are probabilistic — we do not
        guarantee 100% accuracy in entity resolution.
      </Section>

      <Section title="7. Termination">
        Either party may terminate the service agreement with 30 days written notice.
        Upon termination, your API keys will be revoked and access to the platform will
        be suspended. You may request a final export of your enriched data within 30
        days of termination.
      </Section>

      <Section title="8. Contact">
        For questions about these terms, contact us at{' '}
        <A href="mailto:admin@meridianregistry.com">admin@meridianregistry.com</A>.
      </Section>
    </LegalPageLayout>
  );
}
